import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Wallet,
  Receipt,
  Package,
  Star,
  Settings,
  Moon,
  Shield,
  History,
  List,
  MessageCircle,
  Headphones,
  Smartphone,
  Trash2,
  Image,
  BarChart2,
  FileText,
  ScrollText,
  Lock,
  HelpCircle,
  Share2,
  Info,
  Phone,
  Mail,
  Globe,
  Link,
  Bell,
  Tag,
  Layers,
  Map,
  Calendar,
  Clock,
  User,
  UserCheck,
  UserX,
  CreditCard,
  DollarSign,
  TrendingUp,
  Activity,
  PieChart,
  BarChart,
  Database,
  Server,
  Code,
  Terminal,
  Cpu,
  Zap,
  Flag,
  Bookmark,
  Archive,
  Folder,
  FolderOpen,
  Search,
  Filter,
  Edit,
  Eye,
  ChevronRight,
  Home,
  Grid,
  Box,
  Briefcase,
  Award,
  Gift,
  Heart,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Download,
  Upload,
  ExternalLink,
  Key,
  LogOut,
  ShieldCheck,
  ShieldAlert,
  Lightbulb,
  Sparkles,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useSidebarContext } from "../../contexts/SidebarContext";
import { menuApi } from "../../services/api";

const ICON_MAP = {
  // ── Existing ────────────────────────────────────────────────────────────
  dashboard:        LayoutDashboard,
  people:           Users,
  wallet:           Wallet,
  receipt:          Receipt,
  package:          Package,
  star:             Star,
  settings:         Settings,
  moon:             Moon,
  shield:           Shield,
  history:          History,
  list:             List,
  "message-circle": MessageCircle,
  headphones:       Headphones,
  smartphone:       Smartphone,
  "trash-2":        Trash2,
  image:            Image,
  "bar-chart":      BarChart2,
  "file-text":      FileText,
  scroll:           ScrollText,
  lock:             Lock,
  "help-circle":    HelpCircle,

  // ── Newly added ──────────────────────────────────────────────────────────
  "share-2":        Share2,        // Social Links
  info:             Info,          // Contact Support Info
  phone:            Phone,
  mail:             Mail,
  globe:            Globe,
  link:             Link,
  bell:             Bell,
  tag:              Tag,
  layers:           Layers,
  map:              Map,
  calendar:         Calendar,
  clock:            Clock,
  user:             User,
  "user-check":     UserCheck,
  "user-x":         UserX,
  "credit-card":    CreditCard,
  "dollar-sign":    DollarSign,
  "trending-up":    TrendingUp,
  activity:         Activity,
  "pie-chart":      PieChart,
  "bar-chart-2":    BarChart2,
  database:         Database,
  server:           Server,
  code:             Code,
  terminal:         Terminal,
  cpu:              Cpu,
  zap:              Zap,
  flag:             Flag,
  bookmark:         Bookmark,
  archive:          Archive,
  folder:           Folder,
  "folder-open":    FolderOpen,
  search:           Search,
  filter:           Filter,
  edit:             Edit,
  eye:              Eye,
  "chevron-right":  ChevronRight,
  home:             Home,
  grid:             Grid,
  box:              Box,
  briefcase:        Briefcase,
  award:            Award,
  gift:             Gift,
  heart:            Heart,
  "alert-circle":   AlertCircle,
  "alert-triangle": AlertTriangle,
  "check-circle":   CheckCircle,
  "x-circle":       XCircle,
  "refresh-cw":     RefreshCw,
  download:         Download,
  upload:           Upload,
  "external-link":  ExternalLink,
  key:              Key,
  "log-out":        LogOut,
  "shield-check":   ShieldCheck,
  "shield-alert":   ShieldAlert,
  lightbulb:        Lightbulb,
  sparkles:         Sparkles,
};

const resolveIcon = (icon) => ICON_MAP[icon] ?? List;

function buildTree(flatItems) {
  const seen = new Set();
  const unique = [];
  const presorted = [...flatItems].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  presorted.forEach((item) => {
    if (!seen.has(item.path)) {
      seen.add(item.path);
      unique.push(item);
    }
  });

  const byPath = {};
  unique.forEach((item) => {
    byPath[item.path] = { ...item, children: [] };
  });

  const roots = [];
  unique.forEach((item) => {
    const node = byPath[item.path];
    if (item.parentPath) {
      const parentFullPath = "/" + item.parentPath;
      const parent = byPath[parentFullPath];
      if (parent) {
        const alreadyAdded = parent.children.some((c) => c.path === node.path);
        if (!alreadyAdded) parent.children.push(node);
      } else {
        roots.push(node);
      }
    } else {
      roots.push(node);
    }
  });

  const sortLevel = (arr) => {
    arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    arr.forEach((n) => sortLevel(n.children));
    return arr;
  };

  return sortLevel(roots);
}

export default function Sidebar() {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { collapsed, toggle } = useSidebarContext();

  const [menuTree,   setMenuTree]   = useState([]);
  const [menuStatus, setMenuStatus] = useState("loading");
  const [openMenus,  setOpenMenus]  = useState({});
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setMenuStatus("loading");

    menuApi
      .getMyMenu()
      .then((res) => {
        if (cancelled) return null;
        const flat = res.data?.data ?? res.data ?? [];
        const tree = buildTree(Array.isArray(flat) ? flat : []);
        console.log("MENU TREE =", JSON.stringify(tree, null, 2));
        setMenuTree(tree);
        setMenuStatus("ok");
        return tree;
      })
      .then((tree) => {
        if (!tree || cancelled) return;
        const autoOpen = {};
        const walk = (nodes) => {
          nodes.forEach((node) => {
            if (node.children?.length) {
              const hasActive = node.children.some(
                (c) =>
                  location.pathname === c.path ||
                  location.pathname.startsWith(c.path + "/")
              );
              if (hasActive) autoOpen[node.path] = true;
              walk(node.children);
            }
          });
        };
        walk(tree);
        setOpenMenus(autoOpen);
      })
      .catch(() => {
        if (!cancelled) setMenuStatus("error");
      });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!menuTree.length) return;
    setOpenMenus((prev) => {
      const next = { ...prev };
      const walk = (nodes) => {
        nodes.forEach((node) => {
          if (node.children?.length) {
            const hasActive = node.children.some(
              (c) =>
                location.pathname === c.path ||
                location.pathname.startsWith(c.path + "/")
            );
            if (hasActive) next[node.path] = true;
            walk(node.children);
          }
        });
      };
      walk(menuTree);
      return next;
    });
  }, [location.pathname, menuTree]);

  const toggleMenu = useCallback((path) => {
    setOpenMenus((prev) => ({ ...prev, [path]: !prev[path] }));
  }, []);

  const isActive = useCallback(
    (node) => {
      const current = location.pathname;

      if (node.children?.length) {
        return node.children.some(
          (c) => current === c.path || current.startsWith(c.path + "/")
        );
      }

      const isDashboard =
        node.path === "/" ||
        node.path === "/dashboard" ||
        node.title?.toLowerCase() === "dashboard";

      if (isDashboard) {
        return current === "/" || current === "/dashboard";
      }

      return (
        current === node.path ||
        (current.startsWith(node.path + "/") &&
          !menuTree.some(
            (other) =>
              other.path !== node.path &&
              other.path.startsWith(node.path) &&
              (current === other.path || current.startsWith(other.path + "/"))
          ))
      );
    },
    [location.pathname, menuTree]
  );

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      navigate("/login");
    }
  };

  const rowStyle = (active, isChild = false) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: collapsed ? "center" : "flex-start",
    gap: collapsed ? 0 : 10,
    padding: collapsed ? "11px 0" : isChild ? "7px 12px 7px 38px" : "9px 12px",
    borderRadius: "var(--radius, 8px)",
    cursor: "pointer",
    marginBottom: 2,
    background: active ? "#0F3D3E" : "transparent",
    color: active ? "#ffffff" : "var(--text-secondary, #555)",
    fontWeight: active ? 600 : 400,
    fontSize: isChild ? 12 : 13,
    transition: "all 0.15s ease",
    whiteSpace: "nowrap",
    overflow: "hidden",
    userSelect: "none",
  });

  const hoverEnter = (e, active) => {
    if (!active) e.currentTarget.style.background = "rgba(15,61,62,0.07)";
  };

  const hoverLeave = (e, active) => {
    if (!active) e.currentTarget.style.background = "transparent";
  };

  const renderNode = (node, depth = 0) => {
    const active     = isActive(node);
    const hasChildren = node.children?.length > 0;
    const isOpen     = !!openMenus[node.path];
    const isChild    = depth > 0;
    const Icon       = resolveIcon(node.icon);

    return (
      <div key={node.path}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => {
            if (hasChildren && !collapsed) {
              toggleMenu(node.path);
            } else {
              navigate(node.path);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              if (hasChildren && !collapsed) {
                toggleMenu(node.path);
              } else {
                navigate(node.path);
              }
            }
          }}
          title={collapsed ? node.title : ""}
          style={rowStyle(active && !hasChildren, isChild)}
          onMouseEnter={(e) => hoverEnter(e, active && !hasChildren)}
          onMouseLeave={(e) => hoverLeave(e, active && !hasChildren)}
        >
          <span
            style={{
              minWidth: collapsed ? "auto" : isChild ? 16 : 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon size={18} strokeWidth={1.9} />
          </span>

          {!collapsed && (
            <>
              <span style={{ flex: 1 }}>{node.title}</span>
              {hasChildren && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    transition: "transform 0.2s ease",
                    transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                    opacity: 0.5,
                    flexShrink: 0,
                  }}
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              )}
            </>
          )}
        </div>

        {hasChildren && isOpen && !collapsed && (
          <div style={{ overflow: "hidden" }}>
            <div
              style={{
                marginLeft: depth === 0 ? 20 : 32,
                borderLeft: "2px solid rgba(15,61,62,0.15)",
                paddingLeft: 0,
              }}
            >
              {node.children.map((child) => renderNode(child, depth + 1))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      style={{
        width: collapsed ? "72px" : "var(--sidebar-w, 220px)",
        height: "100vh",
        position: "fixed",
        top: 0,
        left: 0,
        background: "var(--bg-sidebar, #fff)",
        borderRight: "1px solid var(--border, #e5e7eb)",
        display: "flex",
        flexDirection: "column",
        zIndex: 100,
        overflow: "hidden",
        transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Logo / header */}
      <div
        style={{
          height: "var(--header-h, 64px)",
          padding: collapsed ? "0 16px" : "0 20px",
          borderBottom: "1px solid var(--border, #e5e7eb)",
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          flexShrink: 0,
          transition: "padding 0.3s ease",
        }}
      >
        {!collapsed && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <div
              style={{
                width: 36, height: 36, borderRadius: 10,
                background: "#0F3D3E",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 17, flexShrink: 0, color: "#fff",
              }}
            >
              ✦
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontFamily: "var(--font-display, serif)",
                  fontSize: 13, fontWeight: 700,
                  color: "#0F3D3E", letterSpacing: "1px", whiteSpace: "nowrap",
                }}
              >
                ASTROVEDA
              </div>
              <div
                style={{
                  fontSize: 9, color: "var(--teal-400, #2dd4bf)",
                  letterSpacing: "2px", textTransform: "uppercase",
                }}
              >
                Admin Panel
              </div>
            </div>
          </div>
        )}

        <button
          onClick={toggle}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          style={{
            background: "none", border: "none", padding: "4px",
            cursor: "pointer", display: "flex",
            alignItems: "center", justifyContent: "center",
            flexShrink: 0, color: "var(--text-muted, #9ca3af)",
            transition: "color 0.15s", lineHeight: 1,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#0F3D3E")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted, #9ca3af)")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg" width="18" height="18"
            viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
          >
            {collapsed
              ? <polyline points="9 18 15 12 9 6" />
              : <polyline points="15 18 9 12 15 6" />
            }
          </svg>
        </button>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "10px" }}>
        {menuStatus === "loading" && (
          <div style={{
            padding: "20px 12px", color: "var(--text-muted, #9ca3af)",
            fontSize: 12, textAlign: "center",
          }}>
            {collapsed ? "..." : "Loading menu..."}
          </div>
        )}

        {menuStatus === "error" && (
          <div style={{
            padding: "12px", margin: "8px",
            borderRadius: "var(--radius, 8px)",
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.2)",
            color: "#dc2626", fontSize: 11, textAlign: "center",
          }}>
            {collapsed ? "!" : (
              <>
                Failed to load menu
                <br />
                <span
                  onClick={() => window.location.reload()}
                  style={{
                    cursor: "pointer", textDecoration: "underline",
                    marginTop: 4, display: "inline-block",
                  }}
                >
                  Retry
                </span>
              </>
            )}
          </div>
        )}

        {menuStatus === "ok" && menuTree.map((node) => renderNode(node))}
      </nav>

      {/* Logout */}
      <div
        style={{
          padding: collapsed ? "12px 0" : "12px 14px",
          borderTop: "1px solid var(--border, #e5e7eb)",
          transition: "padding 0.3s ease",
          display: "flex",
          justifyContent: collapsed ? "center" : "stretch",
        }}
      >
        <button
          onClick={handleLogout}
          disabled={loggingOut}
          title={collapsed ? "Logout" : ""}
          style={{
            width: collapsed ? 40 : "100%",
            height: 36,
            padding: collapsed ? 0 : "8px",
            borderRadius: "var(--radius, 8px)",
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.2)",
            color: "#dc2626",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            transition: "all 0.2s ease",
            fontFamily: "var(--font-body, sans-serif)",
            opacity: loggingOut ? 0.6 : 1,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(239,68,68,0.15)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(239,68,68,0.08)")}
        >
          ⏻ {!collapsed && (loggingOut ? "Logging out..." : "Logout")}
        </button>
      </div>
    </aside>
  );
}